import { Mic } from "lucide-react";

// Keep these in sync with the backend render budget (server.py):
//   MAX_STEP_SECONDS = 16, NARRATION_WORDS_PER_SEC = 2.6  ->  ~42 words spoken per step.
export const NARRATION_WORDS_PER_SEC = 2.6;
export const NARRATION_MAX_SECONDS = 16;
export const NARRATION_MAX_WORDS = Math.round(NARRATION_MAX_SECONDS * NARRATION_WORDS_PER_SEC); // 42

export const countWords = (t) => ((t || "").trim().match(/\S+/g) || []).length;
export const isOverNarration = (t) => countWords(t) > NARRATION_MAX_WORDS;

// Split copy into the spoken portion (whole sentences up to the ~16s budget) and the remainder.
// Mirrors the backend truncation so what you preview is what the narrator reads.
export function splitNarration(text) {
  const t = (text || "").trim();
  if (countWords(t) <= NARRATION_MAX_WORDS) return { kept: t, rest: "" };
  const sentences = t.split(/(?<=[.!?])\s+/);
  const kept = [];
  let count = 0, i = 0;
  for (; i < sentences.length; i++) {
    const wc = countWords(sentences[i]);
    if (kept.length && count + wc > NARRATION_MAX_WORDS) break;
    kept.push(sentences[i]);
    count += wc;
    if (count >= NARRATION_MAX_WORDS) { i++; break; }
  }
  const keptText = kept.join(" ").trim() || t;
  const rest = sentences.slice(i).join(" ").trim();
  return { kept: keptText, rest };
}

// Small live indicator: how much of the typed copy the narrator will actually read (~16s),
// with a friendly reminder that anything beyond that still lives in the PDF.
export function NarrationMeter({ text, className = "" }) {
  const words = countWords(text);
  const budget = NARRATION_MAX_WORDS;
  const narrated = Math.min(words, budget);
  const secs = Math.max(0, Math.round(narrated / NARRATION_WORDS_PER_SEC));
  const over = words > budget;
  const pct = Math.min(100, Math.round((words / budget) * 100) || 0);
  const accent = over ? "#c2410c" : "#4f7746";
  return (
    <div data-testid="narration-meter" className={`mt-1.5 ${className}`}>
      <div className="flex items-center justify-between gap-2 text-[11px] font-semibold" style={{ color: accent }}>
        <span className="flex items-center gap-1">
          <Mic size={12} /> ~{narrated} / {budget} words (~{secs}s)
        </span>
        {over && (
          <span data-testid="narration-over" className="text-[#c2410c]">
            +{words - budget} words → PDF only
          </span>
        )}
      </div>
      <div className="mt-1 relative h-2.5 rounded-full bg-[#0f0f16] overflow-visible" style={{ border: "1px solid #2a3a26" }}>
        <div
          className="h-full rounded-full transition-[width] duration-200"
          style={{ width: `${pct}%`, background: over ? "linear-gradient(90deg,#e0a92e,#c2410c)" : "#1e3aaa" }}
        />
        {pct > 0 && (
          <span
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-4 rounded-full transition-[left] duration-200"
            style={{ left: `${pct}%`, background: "#f5c518", boxShadow: "0 0 6px rgba(245,197,24,0.7)" }}
          />
        )}
      </div>
      {over && (
        <p className="text-[10px] text-[#9a96ac] mt-1 leading-snug">
          Only the first ~{NARRATION_MAX_SECONDS}s is spoken — the rest still appears in your PDF guide.
        </p>
      )}
    </div>
  );
}
